import { React, useEffect, useRef, useState, forwardRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box, Paper, CircularProgress, Backdrop, Slide, Dialog, AppBar, Toolbar, IconButton, Typography, DialogContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import {
    DataGridPro,
    GridToolbar
} from '@mui/x-data-grid-pro';
import axios from 'axios';
import dayjs from 'dayjs';

import { socket } from './socket';

var api = axios.create({ withCredentials: true });

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
}).format;

const numberFormatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
}).format;

const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
};

function DataGrid(props) {
    const { getAccessTokenSilently } = useAuth0();
    const [broker_loading, setBrokerLoading] = useState(true);
    const [columns, setColumns] = useState(null);
    const [rows, setRows] = useState(null);
    const current_broker = props.current_broker;
    const previous_broker = usePrevious(current_broker);
    const isConnected = props.isConnected;
    const [openDialog, setDialogOpen] = useState(false);
    const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState(null);
    const [drilldownColumns, setDrilldownColumns] = useState(null);
    const [drilldownRows, setDrilldownRows] = useState(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [clicked_po, setClickedPO] = useState('Purchase Order Details');

    const handleDialogClickOpen = () => {
        setDialogOpen(true);
    };
    const handleDialogClose = () => {
        setDialogOpen(false);
        setClickedPO('Purchase Order Details');
        setDrilldownColumns(null);
        setDrilldownRows(null);
        setSelectedPurchaseOrderId(null);
    };

    function getData(controller) {
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.get(`${process.env.REACT_APP_BASEURL}/api/datagrid/purchases/${current_broker}`,
                {
                    signal: controller.signal, headers: { Authorization: `Bearer ${token}` }
                }
            ).then((response) => {
                var c = [
                    { 
                        field: 'purchase_order_id', 
                        headerName: 'PO ID', 
                        flex: .25, 
                        minWidth: 100, 
                        type: 'number',
                        renderCell: (params) => {
                            return `${params.value}`;
                        }
                    },
                    { field: 'creation_date', headerName: 'Creation Date', flex: .25, minWidth: 100, type: 'dateTime' },
                    { field: 'event_name', headerName: 'Event', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'event_datetime', headerName: 'Event Date', flex: .25, minWidth: 100, type: 'dateTime' },
                    { field: 'venue_name', headerName: 'Venue', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'total_qty', headerName: 'Total Qty', flex: .25, minWidth: 100, type: 'number' },
                    {
                        field: 'total', headerName: 'Total', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                    {
                        field: 'total_paid', headerName: 'Total Paid', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                    {
                        field: 'balance_due', headerName: 'Balance Due', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                ];
                setColumns(c);

                var r = [];
                response.data.forEach((row, i) => {
                    r.push({
                        id: i,
                        purchase_order_id: row.purchase_order_id,
                        creation_date: dayjs(row.creation_date.replace(/[Z]$/g, '')).toDate(),
                        event_name: row.event_name,
                        event_datetime: dayjs(row.event_datetime.replace(/[Z]$/g, '')).toDate(),
                        venue_name: row.venue_name,
                        total_qty: row.total_qty,
                        total: row.total,
                        total_paid: row.total_paid,
                        balance_due: row.balance_due,
                    });
                });
                setRows(r);
                setBrokerLoading(false);
            }).catch((err) => {
                if (err.message !== 'canceled') {
                    console.log(err);
                    setBrokerLoading(false);
                }
            });
        });
    }

    function getDrilldownData(purchase_order_id) {
        setDrilldownLoading(true);
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            api.get(`${process.env.REACT_APP_BASEURL}/api/datagrid/purchases/drilldown/${purchase_order_id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            ).then((response) => {
                var c = [
                    { field: 'sale_date', headerName: 'Sale Date', flex: .25, minWidth: 100, type: 'dateTime' },
                    { field: 'customer', headerName: 'Customer', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'section', headerName: 'Section', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'row', headerName: 'Row', flex: .25, minWidth: 100, type: 'string' },
                    { field: 'low_seat', headerName: 'Low Seat', flex: .25, minWidth: 100, type: 'number' },
                    { field: 'high_seat', headerName: 'High Seat', flex: .25, minWidth: 100, type: 'number' },
                    { field: 'qty_sold', headerName: 'Qty Sold', flex: .25, minWidth: 100, type: 'number' },
                    {
                        field: 'cost', headerName: 'Cost', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                    {
                        field: 'sold', headerName: 'Sold', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                    {
                        field: 'avg_ticket_sold', headerName: 'Avg Ticket Sold', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                    {
                        field: 'profit_loss', headerName: 'Profit/Loss', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${currencyFormatter(params.value)}`;
                        }
                    },
                    {
                        field: 'profit_margin', headerName: 'Profit Margin %', flex: .25, minWidth: 100, type: 'number',
                        renderCell: (params) => {
                            return `${numberFormatter(params.value)}%`;
                        }
                    },
                ];
                setDrilldownColumns(c);

                var r = [];
                response.data.forEach((row, i) => {
                    r.push({
                        id: i,
                        sale_date: row.sale_date ? dayjs(row.sale_date.replace(/[Z]$/g, '')).toDate() : null,
                        customer: row.customer,
                        section: row.section,
                        row: row.row,
                        low_seat: row.low_seat,
                        high_seat: row.high_seat,
                        qty_sold: row.qty_sold,
                        cost: row.cost,
                        sold: row.sold,
                        avg_ticket_sold: row.avg_ticket_sold,
                        profit_loss: row.profit_loss,
                        profit_margin: row.profit_margin,
                    });
                });
                setDrilldownRows(r);
                setDrilldownLoading(false);
            }).catch((err) => {
                if (err.message !== 'canceled') {
                    console.log(err);
                    setDrilldownLoading(false);
                }
            });
        });
    }

    const handleRowClick = (params) => {
        setSelectedPurchaseOrderId(params.row.purchase_order_id);
        setClickedPO(`PO #${params.row.purchase_order_id} - ${params.row.event_name} @ ${params.row.venue_name}`);
        getDrilldownData(params.row.purchase_order_id);
        handleDialogClickOpen();
    };

    useEffect(() => {
        const controller = new AbortController();

        if (isConnected) {
            if (previous_broker !== current_broker) {
                setBrokerLoading(true);
            }

            getData(controller);

            socket.on('data', () => { getData(controller); });
        }

        return () => {
            socket.off('data');
            controller.abort();
        };
    }, [isConnected, current_broker]);

    if (columns !== null && rows !== null) {
        return (
            <>
                <Backdrop
                    sx={{ color: '#fff', zIndex: 10000000 }}
                    open={broker_loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Dialog
                    fullScreen
                    open={openDialog}
                    onClose={handleDialogClose}
                    TransitionComponent={Transition}
                >
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton
                                edge="start"
                                color="inherit"
                                onClick={handleDialogClose}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                                {clicked_po}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <DialogContent>
                        {drilldownLoading ? (
                            <Box
                                component="main"
                                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                            >
                                <CircularProgress size={60} thickness={4} />
                            </Box>
                        ) : drilldownColumns && drilldownRows ? (
                            <Paper elevation={1}>
                                <div style={{ height: '75vh', width: '100%' }}>
                                    <DataGridPro
                                        slots={{
                                            toolbar: GridToolbar
                                        }}
                                        slotProps={{
                                            toolbar: {
                                                showQuickFilter: true,
                                                quickFilterProps: { debounceMs: 500 }
                                            }
                                        }}
                                        rows={drilldownRows}
                                        columns={drilldownColumns}
                                        pagination
                                        initialState={{
                                            density: "compact"
                                        }}
                                        getRowClassName={(params) => {
                                            return params.id === 'totals-row' ? 'totals-row-bold' : '';
                                        }}
                                        sx={{
                                            '& .totals-row-bold': {
                                                fontWeight: 'bold',
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                            }
                                        }}
                                        pinnedRows={{
                                            bottom: [
                                                {
                                                    id: 'totals-row',
                                                    sale_date: null,
                                                    customer: 'TOTALS',
                                                    section: '',
                                                    row: '',
                                                    low_seat: '',
                                                    high_seat: '',
                                                    qty_sold: drilldownRows.reduce((sum, r) => sum + (r.qty_sold || 0), 0),
                                                    cost: drilldownRows.reduce((sum, r) => sum + (r.cost || 0), 0),
                                                    sold: drilldownRows.reduce((sum, r) => sum + (r.sold || 0), 0),
                                                    avg_ticket_sold: drilldownRows.length > 0 ? drilldownRows.reduce((sum, r) => sum + (r.sold || 0), 0) / drilldownRows.reduce((sum, r) => sum + (r.qty_sold || 0), 0) : 0,
                                                    profit_loss: drilldownRows.reduce((sum, r) => sum + (r.profit_loss || 0), 0),
                                                    profit_margin: drilldownRows.length > 0 ? ((drilldownRows.reduce((sum, r) => sum + (r.profit_loss || 0), 0) / drilldownRows.reduce((sum, r) => sum + (r.cost || 0), 0)) * 100) : 0,
                                                }
                                            ]
                                        }}
                                    />
                                </div>
                            </Paper>
                        ) : (
                            <Box
                                component="main"
                                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                            >
                                <Typography>No drill-down data available</Typography>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
                <Paper elevation={1}>
                    <div style={{ height: '78.5vh', width: '100%' }}>
                        <DataGridPro
                            slots={{
                                toolbar: GridToolbar
                            }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 }
                                }
                            }}
                            onRowClick={handleRowClick}
                            rows={rows}
                            columns={columns}
                            pagination
                        />
                    </div>
                </Paper>
            </>
        );
    } else {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }
}

export default DataGrid;